<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { usePersonalDetailsDisplay } from '@/composables/usePersonalDetailsDisplay'

const props = withDefaults(
  defineProps<{
    showEdit?: boolean
    editLabel?: string
    nameLabel?: string
    addressLabel?: string
    variant?: 'plain' | 'card'
    ariaLabel?: string
    nameTestId?: string
    addressTestId?: string
    editTestId?: string
  }>(),
  {
    showEdit: false,
    editLabel: 'Edit',
    variant: 'plain',
    ariaLabel: 'Your name and home address',
  }
)

const emit = defineEmits<{
  edit: []
}>()

const { t } = useI18n()
const { fullName, addressLines } = usePersonalDetailsDisplay()

const resolvedNameLabel = computed(
  () => props.nameLabel ?? t('Dashboard.Account.Personal.FullName')
)
const resolvedAddressLabel = computed(
  () => props.addressLabel ?? t('Dashboard.Account.Personal.Address')
)
</script>

<template>
  <div
    class="sa-personal-letterhead"
    :class="`sa-personal-letterhead--${variant}`"
    role="group"
    :aria-label="ariaLabel"
  >
    <div class="sa-personal-letterhead__body">
      <div class="sa-personal-letterhead__fields">
        <div class="sa-personal-letterhead__field">
          <span class="sa-detail-label sa-personal-letterhead__label">
            {{ resolvedNameLabel }}
          </span>
          <p class="sa-detail-value sa-personal-letterhead__value" :data-testid="nameTestId">
            {{ fullName }}
          </p>
        </div>

        <div class="sa-personal-letterhead__field">
          <span class="sa-detail-label sa-personal-letterhead__label">
            {{ resolvedAddressLabel }}
          </span>
          <address
            class="sa-personal-letterhead__address"
            :data-testid="addressTestId"
          >
            <span
              v-for="(line, index) in addressLines"
              :key="index"
              class="sa-detail-value sa-personal-letterhead__address-line"
            >
              {{ line }}
            </span>
          </address>
        </div>
      </div>

      <button
        v-if="showEdit"
        type="button"
        class="sa-btn-edit sa-personal-letterhead__edit"
        :data-testid="editTestId"
        @click="emit('edit')"
      >
        {{ editLabel }}
      </button>
    </div>
  </div>
</template>
